/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getInstanceExport, singleResource } from './';
import templateHandlers from './utils/templateProcessors';

const useExport = (resource, instance) => {
  const [exportData, setExportData] = useState(null);
  const [processedData, setProcessedData] = useState(null);
  const [metaData, setMetaData] = useState({
    media: [],
    images: [],
    materials: [],
    eMentor: [],
    focusPoints: []
  });
  const [loading, setLoading] = useState(true);
  const [loadingPhase, setLoadingPhase] = useState(
    `Fetching ${singleResource(resource)} data`
  );
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  // process the exported page data
  const handlePageProcessing = (page, section) => {
    // get the template handler for the page
    const pageHandler = templateHandlers[page.template.label];
    // if the page handler exists, process the page
    const processedPage = pageHandler ? pageHandler(page, section) : page;
    // Extract metadata separately
    setMetaData((prevMetadata) => ({
      media: processedPage.metadata.media
        ? [...prevMetadata.media, ...processedPage.metadata.media]
        : prevMetadata.media,
      images: processedPage.metadata.images
        ? [...prevMetadata.images, ...processedPage.metadata.images]
        : prevMetadata.images,
      materials: processedPage.metadata.materials
        ? [...prevMetadata.materials, ...processedPage.metadata.materials]
        : prevMetadata.materials,
      eMentor: processedPage.metadata.eMentor
        ? [...prevMetadata.eMentor, ...processedPage.metadata.eMentor]
        : prevMetadata.eMentor,
      focusPoints: processedPage.metadata.focusPoints
        ? [...prevMetadata.focusPoints, ...processedPage.metadata.focusPoints]
        : prevMetadata.focusPoints
    }));

    // Remove the TL instructions from the page
    if (processedPage.modifiedPage.instructions) {
      // remove all objects from array in which there is not a key of course that === "Default"
      const instructions = processedPage.modifiedPage.instructions;

      if (Array.isArray(instructions) && instructions.length > 0) {
        const filtered = instructions.filter(
          (instruction) => instruction.course === 'Default'
        );

        if (filtered.length > 0) {
          processedPage.modifiedPage.instructions = filtered;
        }
      }
    } else {
      // remove all objects from array in which there is not a key of course that === "Default"
      const instructions = processedPage.instructions;

      if (Array.isArray(instructions) && instructions.length > 0) {
        const filtered = instructions.filter(
          (instruction) => instruction.course === 'Default'
        );

        if (filtered.length > 0) {
          processedPage.instructions = filtered;
        }
      }
    }

    // return the modified page object, if a handler was applied, otherwise return the original page
    return processedPage.modifiedPage || processedPage;
  };

  // key for getting the individual pe pages. NOTE the rest of the pages will be added to the environment page
  const peTemplateTypeHandlers = {
    'TMP-PE-001': 'static',
    'TMP-PE-002': 'static',
    'TMP-PE-003': 'scenario',
    'TMP-PE-005': 'static',
    'TMP-PE-022': 'static',
    'TMP-PE-023': 'cr',
    'TMP-PE-024': 'cr',
    'TMP-PE-025': 'cr',
    'TMP-PE-026': 'static',
    'TMP-PE-027': 'cr',
    'TMP-PE-028': 'cr',
    'TMP-PE-029': 'static',
    'TMP-PE-030': 'static'
  };

  const handlePEPageProcessing = (pages, section) => {
    // set up page type arrays to parse the pages into group or individual
    let environmentPages = [];
    let individualPages = [];
    let scenarios = [];
    let crPages = [];

    // loop through pages to filter pages as an individual page or a group
    pages.map((page) => {
      let pageType = peTemplateTypeHandlers[page.template.label] || 'grouped';
      let processedPage = handlePageProcessing(page, section);

      // if the page template label is between 004 and 021, treat it as a group
      if (pageType === 'grouped') {
        // replace the scenario content within the processed page
        if (
          processedPage.template.label === 'TMP-PE-004' ||
          processedPage.template.label === 'TMP-PE-006'
        ) {
          processedPage.content.scenario = scenarios;
        }
        environmentPages.push(processedPage);
      } else if (pageType === 'cr') {
        crPages.push(processedPage);
      } else if (pageType === 'scenario') {
        // if the page is a scenario, process the content and add it to the scenarios array
        processedPage.content.core.forEach((contentItem) => {
          scenarios.push({
            text_en: contentItem.description,
            text_tl: contentItem.cc.map((cc) => cc.tl_text).join(' '),
            difficulty: contentItem.difficulty
          });
        });

        processedPage.content.return = pages[0].id;

        individualPages.push(processedPage);
      } else {
        individualPages.push(processedPage);
      }
    });

    // find the env page to build off of
    let envPages = environmentPages.filter(
      (page) =>
        page.template.label === 'TMP-PE-004' ||
        page.template.label === 'TMP-PE-006'
    );

    if (envPages.length === 0) {
      // if no env pages are found, return the individual pages
      return pages;
    }

    envPages.forEach((envPage) => {
      let environment = envPage || null;
      // if its a FPE path, get any immediate pages and add them to the environment page
      if (environment.template.label === 'TMP-PE-006') {
        // check if core array has and items in it
        if (Array.isArray(envPage.content.immediate) && envPage.content.immediate.length > 0) {
          // loop through the core content and replace the object with the matching page data
          envPage.content.immediate.forEach((item, index) => {
            const threadPage = environmentPages.find(
              (page) => page.id === item.id
            );
            // process the thread page if it exists
            environment.content.immediate[index].content = threadPage
              ? threadPage
              : item;

            if (
              item.content.template.label === 'TMP-PE-020'
            ) {
              environment.content.immediate[index].id = item.content.id;
              environment.content.immediate[index].content.content.core.id = environment.id;
              environment.content.immediate[index].content.content.core.required = true;
            }
          });
        }
      }

      if (environment.template.label === 'TMP-PE-004') {
        environment.content.return = pages[0].id;
      }
      // loop through the core content and replace the object with the matching page data
      envPage.content.core.forEach((item, index) => {
        // loop through the core content and replace the object with the matching page data
        item.content.forEach((contentItem, tIndex) => {
          const threadPage = environmentPages.find(
            (page) => page.id === contentItem.id
          );
          // process the thread page if it exists
          environment.content.core[index].content[tIndex].content = threadPage
            ? threadPage
            : contentItem;

          if (
            contentItem.content.template.label === 'TMP-PE-011' ||
            contentItem.content.template.label === 'TMP-PE-017' ||
            contentItem.content.template.label === 'TMP-PE-018' ||
            contentItem.content.template.label === 'TMP-PE-019' ||
            contentItem.content.template.label === 'TMP-PE-020'
          ) {
            environment.content.core[index].id = contentItem.content.id;
          }

          if (
            contentItem.content.template.label === 'TMP-PE-020'
          ) {
            environment.content.core[index].content[tIndex].content.content.core.id = environment.id;
            environment.content.core[index].content[tIndex].content.content.core.required = item.task.type === 'supplemental' ? false : true;
          }
        });
      });
      // add the environment page to the individual pages
      individualPages.push(environment);
    });

    let newCRPage = crPages[0] || null;

    if (newCRPage) {
      const crContent = [];

      crPages.forEach((crPage, index) => {
        let content = {
          ...crPage.content,
          difficulty: index,
          id: crPage.id,
          return: pages[0].id
        };

        if (crPage.template.label === 'TMP-PE-024') {
          content.media.src = crPages[0].content.media.src;
        }

        if (crPage.template.label === 'TMP-PE-025') {
          content.media.media_url =
            'https://base-media-repo.web.app/' +
            crPages[0].label.slice(0, 2).toUpperCase() +
            '/video/' +
            crPages[0].label.toLowerCase().replaceAll('-', '_') +
            '.mp4';

          content.media.src =
            crPages[0].label.toLowerCase().replaceAll('-', '_') + '.mp4';
        }

        crContent.push(content);
      });

      newCRPage.content = crContent;
      // add the cr note card page to the individual pages
      individualPages.push(newCRPage);
    }

    // sort the individual pages by page order
    individualPages.sort((a, b) => a.order - b.order);

    return individualPages;
  };

  // fetch the export data
  const handleFetchExportData = async () => {
    try {
      // Get the export data
      let response = await dispatch(
        getInstanceExport(resource)(instance.id),
        'get'
      ).unwrap();
      setExportData(response);

      // Loop through the sections and pages to process the data
      const processedSections = response.sections.map((section) => {
        // Evaluate if the section label's last 2 characters are "PE"
        if (section.label.slice(-2) === 'PE') {
          return {
            ...section,
            pages: handlePEPageProcessing(section.pages, {
              id: section.id,
              title: section.title
            })
          };
        } else {
          return {
            ...section,
            pages: section.pages.map((page) =>
              handlePageProcessing(page, {
                id: section.id,
                title: section.title
              })
            )
          };
        }
      });

      const processedMaterials = metaData.materials;

      let processedExportData = {
        ...response,
        sections: processedSections
      };
      // Set the loading phase to indicate that the data is being processed
      setLoadingPhase('Processing completed');
      setProcessedData(processedExportData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFetchExportData();
  }, [resource, instance]);

  return {
    exportData,
    processedData,
    metaData,
    loading,
    loadingPhase,
    error
  };
};

export default useExport;
